import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { ActivityActionType, ActivityEntityType } from "@/lib/activityLogger";

export interface ActivityLogEntry {
  id: string;
  org_id: string;
  user_id: string | null;
  action_type: ActivityActionType;
  entity_type: ActivityEntityType | null;
  entity_id: string | null;
  visibility: "private" | "unit" | "building" | "admin";
  metadata: Record<string, unknown>;
  created_at: string;
  // joined
  actor?: { full_name: string | null; email: string | null } | null;
}

export interface ActivityFeedFilters {
  entityType?: ActivityEntityType;
  actionType?: ActivityActionType;
  userId?: string;
  fromDate?: string;
  toDate?: string;
}

const PAGE_SIZE = 20;

export function useActivityFeed(
  orgId: string | null,
  filters: ActivityFeedFilters = {}
) {
  return useInfiniteQuery({
    queryKey: ["activity_feed", orgId, filters],
    enabled: !!orgId,
    initialPageParam: 0,
    getNextPageParam: (lastPage: ActivityLogEntry[], allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from("activity_log")
        .select(`
          *,
          actor:profiles!activity_log_user_id_fkey(full_name, email)
        `)
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .range(pageParam as number, (pageParam as number) + PAGE_SIZE - 1);

      if (filters.entityType) query = query.eq("entity_type", filters.entityType);
      if (filters.actionType)  query = query.eq("action_type", filters.actionType);
      if (filters.userId)      query = query.eq("user_id", filters.userId);
      if (filters.fromDate)    query = query.gte("created_at", filters.fromDate);
      if (filters.toDate)      query = query.lte("created_at", filters.toDate);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ActivityLogEntry[];
    },
  });
}

/** Flat list of all loaded entries (across all pages) */
export function flattenActivityPages(
  pages: ActivityLogEntry[][] | undefined
): ActivityLogEntry[] {
  if (!pages) return [];
  return pages.flat();
}
